import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { useFetchedResource } from "../api/APIContext";
import { useApiService } from "../../../lib/src/api/hooks/useApiService";
import config from "../config";
import { getToken } from "@ventumsoftware/react-toolkit";
import { useNavigate } from "react-router";
import { EmployeeLocation } from "@traas/lib/src/api/dto/EmployeeLocation";
import { useRealtimeMessages } from "@traas/lib/src/realtime/RealtimeConnectionContext";
import { v4 } from "uuid";

interface LiveLocationContextType {
  currentLocations: { [employeeId: string]: EmployeeLocation };
}
export const LiveLocationContext = React.createContext<LiveLocationContextType>({} as LiveLocationContextType);

export const LiveLocationContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { employeeLocationService } = useApiService(config, getToken, useNavigate());
  const history = useFetchedResource<EmployeeLocation[]>(() => employeeLocationService.browse());
  const [currentLocations, setCurrentLocations] = useState<{ [employeeId: string]: EmployeeLocation }>({});
  useEffect(() => {
    history.reload(undefined);
  }, []);
  useEffect(() => {
    const historyMap = history.resource?.reduce(
      (map, location) => {
        if (!map[location.employeeId]) {
          return {
            ...map,
            [location.employeeId]: location,
          };
        } else {
          if (map[location.employeeId].timestamp.getTime() < location.timestamp.getTime()) {
            return {
              ...map,
              [location.employeeId]: location,
            };
          } else {
            return map;
          }
        }
      },
      {} as { [employeeId: string]: EmployeeLocation },
    );
    setCurrentLocations((oldLocations) => ({ ...oldLocations, ...historyMap }));
  }, [history.resource]);

  useRealtimeMessages((json) => {
    if (json.type === "employee_location") {
      const newId = v4();
      setCurrentLocations((old) => ({
        ...old,
        [json.data.employee_id]: {
          id: newId,
          employeeId: json.data.employee_id,
          vehicleId: json.data.vehicle_id,
          lat: json.data.lat,
          lon: json.data.lon,
          angle: json.data.angle,
          timestamp: new Date(json.data.timestamp),
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      }));
    }
  });

  return (
    <LiveLocationContext.Provider
      value={{
        currentLocations,
      }}
    >
      {children}
    </LiveLocationContext.Provider>
  );
};

export const useLiveLocations = () => {
  return useContext(LiveLocationContext);
};
